import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: '', email: '', message: '' };

function useContactSubmit() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const updateField = useCallback((field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  }, []);

  return { form, status, loading, handleSubmit, updateField };
}

function StatusMessage({ status, t }) {
  if (status === 'success') return <p className="mt-4 text-green-400 text-sm text-center" data-testid="contact-success-msg">{t.contact.success}</p>;
  if (status === 'error') return <p className="mt-4 text-red-400 text-sm text-center" data-testid="contact-error-msg">{t.contact.error}</p>;
  return null;
}

export default function ContactForm() {
  const { t } = useLanguage();
  const { form, status, loading, handleSubmit, updateField } = useContactSubmit();

  return (
    <div data-testid="contact-form-wrapper">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
        <div>
          <label className="block text-sm text-gray-400 mb-2">{t.contact.name}</label>
          <Input
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            className="bg-transparent border-white/20 text-white placeholder:text-gray-500 focus:border-[#1a6cf5] focus:ring-1 focus:ring-[#1a6cf5] h-11"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">{t.contact.email}</label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            required
            className="bg-transparent border-white/20 text-white placeholder:text-gray-500 focus:border-[#1a6cf5] focus:ring-1 focus:ring-[#1a6cf5] h-11"
            data-testid="contact-email-input"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">{t.contact.message}</label>
          <Textarea
            value={form.message}
            onChange={(e) => updateField('message', e.target.value)}
            required
            rows={5}
            className="bg-transparent border-white/20 text-white placeholder:text-gray-500 focus:border-[#1a6cf5] focus:ring-1 focus:ring-[#1a6cf5]"
            data-testid="contact-message-input"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#1a6cf5] text-white rounded-full px-8 py-3 font-medium hover:bg-[#3b82f6] transition-all btn-glow disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="contact-submit-btn"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.contact.submit}
        </button>
      </form>

      <p className="mt-4 text-xs text-gray-500 leading-relaxed" data-testid="contact-rgpd-clause">
        Responsable del tratamiento: Skyboard (admin@skyboard.es). Finalidad: gestión y respuesta a su consulta. Legitimación: consentimiento del interesado (art. 6.1.a RGPD). Sus datos no serán cedidos a terceros. Puede ejercer sus derechos de acceso, rectificación, supresión y otros escribiendo a admin@skyboard.es. Más información en nuestra{' '}
        <Link to="/privacy" className="text-[#1a6cf5] hover:underline">Política de Privacidad</Link>.
      </p>

      {status === 'success' && (
        <p className="mt-4 text-green-400 text-sm text-center" data-testid="contact-success-msg">{t.contact.success}</p>
      )}
      {status === 'error' && (
        <p className="mt-4 text-red-400 text-sm text-center" data-testid="contact-error-msg">{t.contact.error}</p>
      )}

      <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-400">
        <Mail className="h-4 w-4" />
        <span>{t.contact.info}{' '}
          <a href="mailto:admin@skyboard.es" className="text-[#1a6cf5] hover:underline">admin@skyboard.es</a>
        </span>
      </div>
    </div>
  );
}
